/**
 * Diagram Fork Service
 *
 * Handles forking diagrams from scenarios and reconciling changes
 * between original and forked diagrams.
 */

import * as Y from 'yjs';
import { ShapeMapAdapter } from '../../../lib/diagram/ShapeMapAdapter';
import { ShapePersistenceGateway } from '../../../lib/diagram/shapeGeometry';
import { readYjsValue } from '../../../lib/diagram/yjsCompat';
import type { Node, Edge } from '../../../core/models/scene';
import { saveScenarioToYDoc, type Scenario } from '../../../core/assistant/stores/scenariosStore';
import { isOrgEdge } from '../types/layout.types';
import { computeFullTreeLayout } from './graphAnalysis';
import type {
  NodeAddition,
  NodeUpdateChange,
  ScenarioDelta,
} from '../../../core/data-profiles/scenarios/ScenarioDelta';
import {
  type DiagramForkMetadata,
  type DiagramDiff,
  type DiagramChange,
  type ConflictInfo,
  type ReconcileOptions,
  type ReconcileResult,
  saveForkMetadata,
} from '../models/DiagramFork';
import { countDeltaChanges } from '../../../core/data-profiles/scenarios/ScenarioDelta';
import { base64ToUint8Array, uint8ArrayToBase64 } from '../../../utils/yjs';
import type { ShapeDataBinding } from '../../../domains/data-binding/models/ShapeDataBinding';
import { toISODateString } from '../../../domains/data-binding/models/ShapeDataBinding';
import { applyOrgChartScenarioFieldUpdate } from './orgChartScenarioFieldSync';
import { recordOrgPlanningLegacyScenarioUsage } from './OrgPlanningLegacyScenarioTelemetry';

const FORK_METADATA_MAP = 'forkMetadata';
const FORK_METADATA_KEY = 'meta';
const REPORTING_EDGE_TYPES = new Set(['reports_to', 'reporting']);

/**
 * Check if moving nodeId to report to newManagerId would create a cycle.
 * Walks the ancestor chain of newManagerId; if nodeId is found, the move is cyclic.
 */
function wouldCreateCycle(
  nodeId: string,
  newManagerId: string,
  parentOf: Map<string, string>,
): boolean {
  const visited = new Set<string>();
  let cur: string | undefined = newManagerId;
  while (cur) {
    if (cur === nodeId) return true;
    if (visited.has(cur)) break;
    visited.add(cur);
    cur = parentOf.get(cur);
  }
  return false;
}

/**
 * Build a parent-of map from the current connectors in the Yjs doc.
 */
function buildParentOfFromConnectors(connectors: Y.Map<unknown>): Map<string, string> {
  const parentOf = new Map<string, string>();
  connectors.forEach((raw) => {
    if (!raw || typeof raw !== 'object') return;
    const edge = raw as Record<string, unknown>;
    const fromId = (edge.fromId ?? edge.from) as string | undefined;
    const toId = (edge.toId ?? edge.to) as string | undefined;
    const edgeType = edge.type as string;
    if (fromId && toId && REPORTING_EDGE_TYPES.has(edgeType)) {
      parentOf.set(fromId, toId);
    }
  });
  return parentOf;
}

/**
 * Hydrate all CSV-cached branches containing nodes affected by a scenario delta.
 * Must be called before applyScenarioDeltaToYDoc so that shapes/connectors exist
 * in Y.Doc for removals, moves, and updates to operate on.
 */
export function hydrateAffectedCsvBranches(
  doc: Y.Doc,
  delta: ScenarioDelta<Record<string, unknown>, NodeUpdateChange>,
): void {
  const csvRoot = doc.getMap('orgchartCsvCache') as Y.Map<unknown> | undefined;
  if (!csvRoot) return;
  const csvChildren = csvRoot.get('children') as Y.Map<unknown> | undefined;
  const csvRecords = csvRoot.get('records') as Y.Map<unknown> | undefined;
  if (!csvChildren || !csvRecords) return;

  const affectedNodeIds = new Set<string>();
  delta.additions?.forEach(a => {
    if (a.managerId) affectedNodeIds.add(a.managerId as string);
  });
  delta.removals?.forEach(r => {
    if (r.nodeId) affectedNodeIds.add(r.nodeId as string);
  });
  delta.moves?.forEach(m => {
    if (m.nodeId) affectedNodeIds.add(m.nodeId as string);
    if (m.newManagerId) affectedNodeIds.add(m.newManagerId as string);
  });
  delta.updates?.forEach(u => {
    if (u.nodeId) affectedNodeIds.add(u.nodeId as string);
  });
  if (affectedNodeIds.size === 0) return;

  const managerOf = new Map<string, string>();
  const connectors = doc.getMap('connectors');
  connectors.forEach((raw) => {
    if (!raw || typeof raw !== 'object') return;
    const edge = raw as Record<string, unknown>;
    const from = (edge.fromId ?? edge.from) as string | undefined;
    const to = (edge.toId ?? edge.to) as string | undefined;
    const edgeType = edge.type as string;
    if (from && to && REPORTING_EDGE_TYPES.has(edgeType)) {
      managerOf.set(from, to);
    }
  });
  csvChildren.forEach((childIds: unknown, managerId: string) => {
    const ids = childIds as string[] | undefined;
    if (!ids) return;
    for (const childId of ids) {
      if (!managerOf.has(childId)) managerOf.set(childId, managerId);
    }
  });

  const shapes = new ShapePersistenceGateway(new ShapeMapAdapter(doc.getMap('shapes')));
  const collapsedSet = new Set<string>();
  csvChildren.forEach((_: unknown, nodeId: string) => {
    if (!shapes.get(nodeId)) return;
    const kids = csvChildren.get(nodeId) as string[] | undefined;
    if (!kids || kids.length === 0) return;
    const hasUnhydratedChild = kids.some(kid => !shapes.get(kid));
    if (hasUnhydratedChild) collapsedSet.add(nodeId);
  });

  const nodesToHydrate = new Set<string>();
  for (const nodeId of affectedNodeIds) {
    let current = nodeId;
    const path: string[] = [];
    const visited = new Set<string>();
    while (current) {
      if (visited.has(current)) break;
      visited.add(current);
      path.push(current);
      if (collapsedSet.has(current)) break;
      const parent = managerOf.get(current);
      if (!parent || parent === current) break;
      current = parent;
    }
    for (const pathNode of path) {
      nodesToHydrate.add(pathNode);
    }
  }

  if (nodesToHydrate.size === 0) return;

  doc.transact(() => {
    for (const nodeId of nodesToHydrate) {
      const childNodeIds = csvChildren.get(nodeId) as string[] | undefined;
      if (!childNodeIds || childNodeIds.length === 0) continue;

      for (const childId of childNodeIds) {
        if (shapes.get(childId)) continue;
        const record = csvRecords.get(childId) as Record<string, unknown> | undefined;
        if (!record) continue;
        const name = typeof record.name === 'string' ? record.name : childId;
        const role = typeof record.role === 'string' ? record.role : (typeof record.title === 'string' ? record.title : undefined);
        const personId = typeof record.personId === 'string' ? record.personId : childId;

        shapes.create({
          id: childId,
          type: 'org_person_text',
          category: 'orgchart',
          name,
          x: 0,
          y: 0,
          w: 270,
          h: 80,
          width: 270,
          height: 80,
          attrs: { ...record, personId, name, role },
        });

        const edgeId = `edge-csv-${childId}-${nodeId}`;
        if (!connectors.get(edgeId)) {
          connectors.set(edgeId, {
            id: edgeId,
            type: 'reporting',
            from: childId,
            to: nodeId,
            fromId: childId,
            toId: nodeId,
            fromAnchor: 'top',
            toAnchor: 'bottom',
            attrs: { relation: 'reporting' },
          });
        }
      }
    }
  }, 'orgchart-csv-hydrate-for-apply');
}

interface StoredForkMetadata {
  forkedFromDiagramId: string;
  forkedFromFolderId?: string;
  forkedFromDiagramName: string;
  forkedAtStateVector: string;
  forkedAtTimestamp: string;
  scenarioName: string;
  scenarioDescription?: string;
  changeCount: number;
  forkedAtSnapshot?: string;
}

// =============================================================================
// Fork Creation
// =============================================================================

export interface ForkDiagramParams {
  /** Current diagram ID being forked from */
  sourceDiagramId: string;

  /** Folder ID containing the source diagram */
  sourceFolderId?: string;

  /** Name of the source diagram */
  sourceDiagramName: string;

  /** The Yjs document of the source diagram (used for state vector) */
  sourceYDoc: Y.Doc;

  /** Current nodes in the diagram (from store) */
  sourceNodes: Node[];

  /** Current edges in the diagram (from store) */
  sourceEdges: Edge[];

  /** The scenario to apply */
  scenario: Scenario;

  /** ID for the new forked diagram (generated externally) */
  newDiagramId: string;

  /**
   * 'plan' (linked working copy) keeps the changes as a live scenario so they
   * can still be applied to the org chart from the new diagram. 'copy'
   * (default) bakes them in as a frozen, disconnected point-in-time copy.
   */
  mode?: 'plan' | 'copy';
}

export interface ForkDiagramResult {
  /** The server copied the selected protected scenario, including succession. */
  carriedSourceScenario?: boolean;
  success: boolean;
  newDiagramId?: string;
  /** Base64-encoded Yjs document snapshot for the new diagram */
  ydocSnapshot?: string;
  /** Base64-encoded state vector */
  stateVector?: string;
  error?: string;
}

/**
 * Persist fork metadata into the Y.Doc so fork lineage survives across devices/sessions.
 */
export function saveForkMetadataToYDoc(doc: Y.Doc, metadata: DiagramForkMetadata): void {
  const map = doc.getMap<unknown>(FORK_METADATA_MAP);
  const stored: StoredForkMetadata = {
    forkedFromDiagramId: metadata.forkedFromDiagramId,
    forkedFromFolderId: metadata.forkedFromFolderId,
    forkedFromDiagramName: metadata.forkedFromDiagramName,
    forkedAtStateVector: metadata.forkedAtStateVector,
    forkedAtTimestamp: metadata.forkedAtTimestamp.toISOString(),
    scenarioName: metadata.scenarioName,
    scenarioDescription: metadata.scenarioDescription,
    changeCount: metadata.changeCount,
    forkedAtSnapshot: metadata.forkedAtSnapshot,
  };
  doc.transact(() => {
    map.set(FORK_METADATA_KEY, stored);
  }, 'fork-metadata-save');
}

/**
 * Load fork metadata from Y.Doc (preferred source for cross-device persistence).
 */
export function loadForkMetadataFromYDoc(doc: Y.Doc): DiagramForkMetadata | null {
  try {
    const map = doc.getMap<unknown>(FORK_METADATA_MAP);
    const raw = map.get(FORK_METADATA_KEY);
    if (!raw || typeof raw !== 'object') return null;

    const parsed = raw as StoredForkMetadata;
    if (!parsed.forkedFromDiagramId) return null;
    return {
      forkedFromDiagramId: parsed.forkedFromDiagramId,
      forkedFromFolderId: parsed.forkedFromFolderId,
      forkedFromDiagramName: parsed.forkedFromDiagramName,
      forkedAtStateVector: parsed.forkedAtStateVector,
      forkedAtTimestamp: new Date(parsed.forkedAtTimestamp),
      scenarioName: parsed.scenarioName,
      scenarioDescription: parsed.scenarioDescription,
      changeCount: parsed.changeCount,
      forkedAtSnapshot: parsed.forkedAtSnapshot,
    };
  } catch {
    return null;
  }
}

/**
 * Build a Y.Doc from a base64-encoded snapshot.
 */
export function createDocFromSnapshot(snapshotBase64: string): Y.Doc {
  const doc = new Y.Doc();
  const bytes = base64ToUint8Array(snapshotBase64);
  Y.applyUpdate(doc, bytes);
  return doc;
}

function cloneYValue(value: unknown): unknown {
  if (value instanceof Y.Map) {
    const cloned = new Y.Map<unknown>();
    value.forEach((childValue, childKey) => {
      cloned.set(childKey, cloneYValue(childValue));
    });
    return cloned;
  }

  if (value instanceof Y.Array) {
    const cloned = new Y.Array<unknown>();
    const items = value.toArray().map((item) => cloneYValue(item));
    if (items.length > 0) cloned.insert(0, items);
    return cloned;
  }

  if (value instanceof Uint8Array) {
    return value.slice();
  }

  if (Array.isArray(value)) {
    return value.map((item) => cloneYValue(item));
  }

  if (isObjectRecord(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, childValue]) => [key, cloneYValue(childValue)]),
    );
  }

  return value;
}

function copySharedYMap(
  sourceDoc: Y.Doc,
  targetDoc: Y.Doc,
  mapName: string,
  transformEntry?: (key: string, value: unknown) => unknown,
): void {
  const sourceMap = sourceDoc.getMap<unknown>(mapName);
  if (sourceMap.size === 0) return;

  const targetMap = targetDoc.getMap<unknown>(mapName);
  targetDoc.transact(() => {
    sourceMap.forEach((value, key) => {
      const nextValue = transformEntry ? transformEntry(key, value) : value;
      targetMap.set(key, cloneYValue(nextValue));
    });
  }, `copy-${mapName}`);
}

function sanitizeForkDiagramSettings(settingsJson: unknown): unknown {
  if (typeof settingsJson !== 'string') return settingsJson;

  try {
    const parsed = JSON.parse(settingsJson) as Record<string, unknown>;
    if (!isObjectRecord(parsed)) return settingsJson;

    const orgchart = isObjectRecord(parsed.orgchart)
      ? { ...parsed.orgchart }
      : undefined;
    if (orgchart) delete orgchart.focusScope;

    return JSON.stringify({
      ...parsed,
      ...(orgchart ? { orgchart } : {}),
    });
  } catch {
    return settingsJson;
  }
}

function copyOrgChartForkState(sourceDoc: Y.Doc, targetDoc: Y.Doc): void {
  copySharedYMap(sourceDoc, targetDoc, 'orgchartCsvCache');
  copySharedYMap(sourceDoc, targetDoc, 'diagramSettings', (key, value) => {
    if (key === 'settings') {
      return sanitizeForkDiagramSettings(value);
    }
    return value;
  });
}

/**
 * Create a fork of a diagram with a scenario applied.
 *
 * This function:
 * 1. Creates a new Yjs document with the current diagram content
 * 2. Applies the scenario delta to create the forked state
 * 3. Returns the snapshot to be saved to the new diagram
 * 4. Stores fork metadata for later reconciliation
 */
export function createDiagramFork(params: ForkDiagramParams): ForkDiagramResult {
  const {
    sourceDiagramId,
    sourceFolderId,
    sourceDiagramName,
    sourceYDoc,
    sourceNodes,
    sourceEdges,
    scenario,
    newDiagramId,
  } = params;

  try {
    if (sourceNodes.some((node) => node.lockOwner === 'projection' || node.attrs?.folderRecordId)) {
      return { success: false, error: 'Source-backed charts require a protected planning artifact or an authorized export; they cannot be copied into Yjs.' };
    }
    // 1. Capture the current state vector for reconciliation tracking
    const sourceStateVector = Y.encodeStateVector(sourceYDoc);
    const sourceStateVectorBase64 = uint8ArrayToBase64(sourceStateVector);

    // 2. Build base snapshot at fork point (for future compare/reconcile)
    const baseDoc = new Y.Doc();
    populateYDocFromContent(baseDoc, sourceNodes, sourceEdges);
    copyOrgChartForkState(sourceYDoc, baseDoc);
    const baseSnapshot = Y.encodeStateAsUpdate(baseDoc);

    // 3. Create a new Y.Doc and populate it with current diagram content
    const forkedDoc = new Y.Doc();

    // Populate with current nodes and edges from the store
    // This ensures we have the actual diagram content, not just what's in the Y.Doc
    populateYDocFromContent(forkedDoc, sourceNodes, sourceEdges);
    copyOrgChartForkState(sourceYDoc, forkedDoc);

    // 4. Carry the scenario across.
    //    'plan'  — the new diagram stays a working surface: the changes arrive
    //              as a live scenario (record bindings intact), so they can
    //              still be applied to the org chart from there.
    //    'copy'  — a frozen, disconnected copy: the changes are baked into the
    //              content, so it reads as a point-in-time export.
    if (params.mode === 'plan') {
      saveScenarioToYDoc(forkedDoc, scenario);
    } else {
      applyScenarioDeltaToYDocWithLayout(forkedDoc, scenario.delta);
    }

    // 5. Store fork metadata
    const forkMetadata: DiagramForkMetadata = {
      forkedFromDiagramId: sourceDiagramId,
      forkedFromFolderId: sourceFolderId,
      forkedFromDiagramName: sourceDiagramName,
      forkedAtStateVector: sourceStateVectorBase64,
      forkedAtTimestamp: new Date(),
      scenarioName: scenario.name,
      scenarioDescription: scenario.description,
      changeCount: countDeltaChanges(scenario.delta),
      forkedAtSnapshot: uint8ArrayToBase64(baseSnapshot),
    };
    saveForkMetadataToYDoc(forkedDoc, forkMetadata);
    saveForkMetadata(newDiagramId, forkMetadata);

    // 6. Encode the forked document
    const forkedSnapshot = Y.encodeStateAsUpdate(forkedDoc);
    const forkedStateVector = Y.encodeStateVector(forkedDoc);

    // Cleanup
    baseDoc.destroy();
    forkedDoc.destroy();

    return {
      success: true,
      newDiagramId,
      ydocSnapshot: uint8ArrayToBase64(forkedSnapshot),
      stateVector: uint8ArrayToBase64(forkedStateVector),
    };
  } catch (error) {
    console.error('[DiagramForkService] Failed to create fork:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Populate a Yjs document with nodes and edges content
 */
function populateYDocFromContent(doc: Y.Doc, nodes: Node[], edges: Edge[]): void {
  const shapes = new ShapePersistenceGateway(new ShapeMapAdapter(doc.getMap('shapes')));
  const connectors = doc.getMap('connectors');
  const shapeRecords = nodes.map(nodeToYjsData);
  shapes.validateCreates(shapeRecords);

  doc.transact(() => {
    // Add all nodes as shapes
    for (const shape of shapeRecords) {
      shapes.create(shape);
    }

    // Add all edges as connectors
    for (const edge of edges) {
      connectors.set(edge.id, edgeToYjsData(edge));
    }
  }, 'populate-fork');
}

/**
 * Convert an Edge to Yjs-compatible data
 */
function edgeToYjsData(edge: Edge): Record<string, unknown> {
  return {
    id: edge.id,
    type: edge.type,
    from: edge.from,
    to: edge.to,
    fromId: edge.from, // Some code uses fromId/toId
    toId: edge.to,
    fromAnchor: edge.fromAnchor,
    toAnchor: edge.toAnchor,
    attrs: edge.attrs,
  };
}

/**
 * Patch a shape's dataBinding.localChanges with scenario-driven field changes.
 * Returns the shape unchanged if it has no binding.
 */
function patchBindingLocalChanges(
  shape: Record<string, unknown>,
  changes: Record<string, unknown>,
): Record<string, unknown> {
  const props = shape.props as Record<string, unknown> | undefined;
  const binding = props?.dataBinding as ShapeDataBinding | undefined;
  if (!binding || !props) return shape;

  const now = toISODateString(new Date());
  const existingFields = binding.localChanges?.fields ?? {};
  const updatedBinding: ShapeDataBinding = {
    ...binding,
    localChanges: {
      fields: { ...existingFields, ...changes },
      changedAt: now,
      changedBy: 'scenario',
    },
    localVersion: binding.localVersion + 1,
  };

  return {
    ...shape,
    props: { ...props, dataBinding: updatedBinding },
  };
}

/**
 * Apply a scenario delta to a Yjs document
 */
export function applyScenarioDeltaToYDoc(
  doc: Y.Doc,
  delta: ScenarioDelta<Record<string, unknown>, NodeUpdateChange>,
  options?: { trackBindingLocalChanges?: boolean; transactionOrigin?: string },
): void {
  const trackBindingLocalChanges = options?.trackBindingLocalChanges ?? true;
  const transactionOrigin = options?.transactionOrigin ?? 'scenario-fork';
  const shapes = new ShapePersistenceGateway(new ShapeMapAdapter(doc.getMap('shapes')));
  const connectors = doc.getMap('connectors');
  const additions = delta.additions.map(additionToYjsNode);
  shapes.validateCreates(additions);

  const removedNodeIds = new Set(delta.removals.map((r) => r.nodeId as string));

  doc.transact(() => {
    // Apply additions (nodes)
    for (const nodeData of additions) {
      shapes.create(nodeData);
    }

    // Materialize reporting connectors for additions with manager references.
    // Scenario preview already synthesizes these edges; live apply must persist them.
    for (const addition of delta.additions) {
      const fromId = addition.id as string;
      const toId = addition.managerId as string | undefined;
      if (!toId) continue;
      if (!shapes.has(fromId) || !shapes.has(toId)) continue;

      let existingConnectorKey: string | null = null;
      connectors.forEach((rawConnector, key) => {
        if (existingConnectorKey) return;
        const connector = readYjsValue<Record<string, unknown>>(rawConnector);
        if (!isObjectRecord(connector) || !isReportingConnector(connector)) return;
        const { from } = readConnectorEndpoints(connector);
        if (from === fromId) {
          existingConnectorKey = key;
        }
      });

      const connectorId = existingConnectorKey ?? `edge-${addition.id}-${addition.managerId}`;
      connectors.set(connectorId, {
        id: connectorId,
        type: 'reporting',
        from: fromId,
        to: toId,
        fromId,
        toId,
        fromAnchor: 'top',
        toAnchor: 'bottom',
        attrs: {
          relation: 'reports_to',
          routingType: 'rounded',
          endArrow: { type: 'triangle', size: 8 },
        },
      });
    }

    // Apply removals — delete shapes and their associated connectors
    for (const removal of delta.removals) {
      shapes.delete(removal.nodeId as string);
    }
    if (removedNodeIds.size > 0) {
      const connectorKeysToDelete: string[] = [];
      connectors.forEach((raw, key) => {
        if (!raw || typeof raw !== 'object') return;
        const edge = raw as Record<string, unknown>;
        const from = (edge.fromId ?? edge.from) as string | undefined;
        const to = (edge.toId ?? edge.to) as string | undefined;
        if ((from && removedNodeIds.has(from)) || (to && removedNodeIds.has(to))) {
          connectorKeysToDelete.push(key);
        }
      });
      for (const key of connectorKeysToDelete) {
        connectors.delete(key);
      }
    }

    // Apply updates
    for (const update of delta.updates) {
      const existingNode = shapes.get(update.nodeId as string);
      if (existingNode && typeof existingNode === 'object') {
        const { field, newValue } = update.change;

        let nodeData = applyOrgChartScenarioFieldUpdate(
          { ...(existingNode as Record<string, unknown>) },
          field,
          newValue,
        );

        if (trackBindingLocalChanges) {
          nodeData = patchBindingLocalChanges(nodeData, { [field]: newValue });
        }

        shapes.patch(update.nodeId as string, nodeData);
      }
    }

    // Apply moves (update reporting relationships). Some valid scenarios include
    // dependent move chains, so we repeatedly apply any move that is safe in the
    // current parent map instead of trusting the incoming order.
    const currentParentOf = buildParentOfFromConnectors(connectors);
    const pendingMoves = [...delta.moves];
    const appliedMoves: typeof delta.moves = [];

    const applyMove = (move: typeof delta.moves[number]) => {
      const edgesToUpdate: Array<{ key: string; newData: Record<string, unknown> }> = [];
      const edgesToDelete: string[] = [];

      connectors.forEach((value, key) => {
        const edge = readYjsValue<Record<string, unknown>>(value);
        if (edge && typeof edge === 'object') {
          const fromId = edge.fromId ?? edge.from;
          const edgeType = edge.type as string;
          const edgeObj = edge as unknown as Edge;
          if (fromId === move.nodeId && (isOrgEdge(edgeObj) || edgeType === 'reports_to')) {
            if (move.newManagerId) {
              edgesToUpdate.push({
                key,
                newData: { ...edge, toId: move.newManagerId, to: move.newManagerId },
              });
            } else {
              edgesToDelete.push(key);
            }
          }
        }
      });

      for (const { key, newData } of edgesToUpdate) {
        connectors.set(key, newData);
      }
      for (const key of edgesToDelete) {
        connectors.delete(key);
      }

      if (move.newManagerId && edgesToUpdate.length === 0) {
        const newEdgeId = `edge-move-${move.nodeId}-${move.newManagerId}`;
        connectors.set(newEdgeId, {
          id: newEdgeId,
          type: 'reporting',
          from: move.nodeId as string,
          to: move.newManagerId as string,
          fromId: move.nodeId as string,
          toId: move.newManagerId as string,
          fromAnchor: 'top',
          toAnchor: 'bottom',
          attrs: { routingType: 'rounded', relation: 'reporting' },
        });
      }

      if (trackBindingLocalChanges) {
        const movedShape = shapes.get(move.nodeId as string);
        if (movedShape && typeof movedShape === 'object') {
          const patched = patchBindingLocalChanges(
            { ...(movedShape as Record<string, unknown>) },
            { reportsTo: move.newManagerId },
          );
          shapes.patch(move.nodeId as string, patched);
        }
      }

      if (move.newManagerId) {
        currentParentOf.set(move.nodeId as string, move.newManagerId as string);
      } else {
        currentParentOf.delete(move.nodeId as string);
      }

      appliedMoves.push(move);
    };

    while (pendingMoves.length > 0) {
      let appliedThisPass = false;

      for (let index = 0; index < pendingMoves.length;) {
        const move = pendingMoves[index];
        if (move.newManagerId && wouldCreateCycle(move.nodeId as string, move.newManagerId as string, currentParentOf)) {
          index += 1;
          continue;
        }

        applyMove(move);
        pendingMoves.splice(index, 1);
        appliedThisPass = true;
      }

      if (!appliedThisPass) {
        console.warn('[DiagramForkService] Skipping cyclic scenario moves', pendingMoves.map((move) => ({
          nodeId: move.nodeId,
          newManagerId: move.newManagerId,
        })));
        break;
      }
    }

    // Apply explicit edge additions
    for (const edgeAdd of delta.edgeAdditions) {
      const relation = String(edgeAdd.props?.relation ?? edgeAdd.type);
      const successionEdge = relation === 'successor_of' || relation === 'successor_for';
      const edgeData = {
        id: edgeAdd.id as string,
        type: edgeAdd.type || 'reporting',
        from: edgeAdd.from as string,
        to: edgeAdd.to as string,
        fromId: edgeAdd.from as string,
        toId: edgeAdd.to as string,
        fromAnchor: successionEdge ? 'right' : 'top',
        toAnchor: successionEdge ? 'right' : 'bottom',
        attrs: {
          routingType: 'rounded',
          ...(edgeAdd.props ?? {}),
        },
      };
      connectors.set(edgeAdd.id as string, edgeData);
    }

    // Apply explicit edge removals
    for (const edgeRem of delta.edgeRemovals) {
      connectors.delete(edgeRem.edgeId as string);
    }

    // Sync orgchartCsvCache so collapsed/unhydrated branches reflect the changes
    const csvRoot = doc.getMap('orgchartCsvCache') as Y.Map<unknown> | undefined;
    const csvChildren = csvRoot?.get('children') as Y.Map<unknown> | undefined;
    const csvRecords = csvRoot?.get('records') as Y.Map<unknown> | undefined;
    const csvTotals = csvRoot?.get('totals') as Y.Map<unknown> | undefined;

    if (csvChildren) {
      const affectedParentIds = new Set<string>();

      for (const addition of delta.additions) {
        const nodeId = addition.id as string;
        csvRecords?.set(nodeId, additionToCsvCacheRecord(addition));
        writeCsvChildIds(csvChildren, nodeId, readCsvChildIds(csvChildren, nodeId));
        csvTotals?.set(nodeId, 0);

        if (addition.managerId && addCsvChild(csvChildren, addition.managerId as string, nodeId)) {
          affectedParentIds.add(addition.managerId as string);
        }
      }

      for (const removal of delta.removals) {
        const nodeId = removal.nodeId as string;
        csvRecords?.delete(nodeId);
        csvChildren.delete(nodeId);
        csvTotals?.delete(nodeId);
        csvChildren.forEach((childIds: unknown, parentId: string) => {
          const ids = childIds as string[] | undefined;
          if (!ids || !Array.isArray(ids)) return;
          if (ids.includes(nodeId)) {
            if (!removeCsvChild(csvChildren, parentId, nodeId)) return;
            affectedParentIds.add(parentId);
          }
        });
      }

      for (const move of appliedMoves) {
        const nodeId = move.nodeId as string;
        const newManagerId = move.newManagerId as string | null;
        csvChildren.forEach((childIds: unknown, parentId: string) => {
          const ids = childIds as string[] | undefined;
          if (!ids || !Array.isArray(ids)) return;
          if (ids.includes(nodeId)) {
            if (!removeCsvChild(csvChildren, parentId, nodeId)) return;
            affectedParentIds.add(parentId);
          }
        });
        if (newManagerId) {
          if (addCsvChild(csvChildren, newManagerId, nodeId)) {
            affectedParentIds.add(newManagerId);
          }
        }

        const existingRecord = csvRecords?.get(nodeId);
        if (isObjectRecord(existingRecord)) {
          csvRecords?.set(nodeId, {
            ...existingRecord,
            managerId: newManagerId ?? undefined,
            reportsTo: newManagerId ?? undefined,
          });
        }
      }

      if (csvTotals && affectedParentIds.size > 0) {
        const recomputeTotal = (nodeId: string, visited: Set<string>): number => {
          if (visited.has(nodeId)) return 0;
          visited.add(nodeId);
          const kids = csvChildren.get(nodeId) as string[] | undefined;
          if (!kids || !Array.isArray(kids) || kids.length === 0) return 0;
          let total = 0;
          for (const kid of kids) {
            total += 1 + recomputeTotal(kid, visited);
          }
          return total;
        };

        const buildParentMap = (): Map<string, string> => {
          const parentOf = new Map<string, string>();
          csvChildren.forEach((childIds: unknown, parentId: string) => {
            const ids = childIds as string[] | undefined;
            if (!ids || !Array.isArray(ids)) return;
            for (const cid of ids) {
              parentOf.set(cid, parentId);
            }
          });
          return parentOf;
        };
        const parentOf = buildParentMap();

        const toRecompute = new Set<string>();
        for (const pid of affectedParentIds) {
          let cur: string | undefined = pid;
          while (cur) {
            if (toRecompute.has(cur)) break;
            toRecompute.add(cur);
            cur = parentOf.get(cur);
          }
        }

        for (const nodeId of toRecompute) {
          const visited = new Set<string>();
          const newTotal = recomputeTotal(nodeId, visited);
          csvTotals.set(nodeId, newTotal);
        }
      }

      if (csvRoot) {
        csvRoot.set('_syncRev', Date.now());
      }
    }

    // Apply explicit edge additions from scenario deltas.
    for (const edgeAddition of delta.edgeAdditions) {
      const fromId = edgeAddition.from as string;
      const toId = edgeAddition.to as string;
      if (!shapes.has(fromId) || !shapes.has(toId)) continue;
      const relation = String(edgeAddition.props?.relation ?? edgeAddition.type);
      const successionEdge = relation === 'successor_of' || relation === 'successor_for';
      connectors.set(edgeAddition.id as string, {
        id: edgeAddition.id,
        type: edgeAddition.type ?? 'reporting',
        from: fromId,
        to: toId,
        fromId,
        toId,
        fromAnchor: successionEdge ? 'right' : 'top',
        toAnchor: successionEdge ? 'right' : 'bottom',
        attrs: {
          relation: edgeAddition.type === 'reports_to' ? 'reports_to' : undefined,
          ...(edgeAddition.props as Record<string, unknown> | undefined),
        },
      });
    }

    // Apply explicit edge removals from scenario deltas.
    for (const edgeRemoval of delta.edgeRemovals) {
      connectors.delete(edgeRemoval.edgeId as string);
    }
  }, transactionOrigin);
}

/**
 * Apply a scenario delta to a Y.Doc and then compute a full tree layout,
 * writing the resulting positions back into the same transaction.
 * This guarantees every addition has correct x/y when the doc is persisted.
 */
export function applyScenarioDeltaToYDocWithLayout(
  doc: Y.Doc,
  delta: ScenarioDelta<Record<string, unknown>, NodeUpdateChange>,
  options?: { trackBindingLocalChanges?: boolean; transactionOrigin?: string },
): void {
  recordOrgPlanningLegacyScenarioUsage('presentation_only', 'diagram_scenario_delta');
  const changesHierarchy = scenarioDeltaChangesOrgHierarchy(doc, delta);
  applyScenarioDeltaToYDoc(doc, delta, options);

  // Relationship-only scenarios (for example successor_of nominations) do
  // not change the reporting tree. Re-running the full tree layout here would
  // overwrite every authored card position for a non-structural edit.
  if (!changesHierarchy) return;

  try {
    const { nodes, edges } = extractSceneFromYDoc(doc);
    const layoutPositions = computeFullTreeLayout(nodes, edges);

    if (layoutPositions.size === 0) return;

    const shapes = new ShapePersistenceGateway(new ShapeMapAdapter(doc.getMap('shapes')));
    const transactionOrigin = options?.transactionOrigin ?? 'scenario-layout';

    doc.transact(() => {
      layoutPositions.forEach((pos, id) => {
        const existing = shapes.get(id);
        if (existing && typeof existing === 'object') {
          shapes.patch(id, { x: pos.x, y: pos.y });
        }
      });
    }, transactionOrigin);
  } catch (err) {
    console.error('[DiagramForkService] Layout computation failed after applying delta; positions not updated:', err);
  }
}

function scenarioDeltaChangesOrgHierarchy(
  doc: Y.Doc,
  delta: ScenarioDelta<Record<string, unknown>, NodeUpdateChange>,
): boolean {
  if (delta.additions.length > 0 || delta.removals.length > 0 || delta.moves.length > 0) {
    return true;
  }

  const addedReportingEdge = delta.edgeAdditions.some((addition) => {
    const type = String(addition.type ?? '');
    const relation = String((addition.props as Record<string, unknown> | undefined)?.relation ?? '');
    return REPORTING_EDGE_TYPES.has(type) || REPORTING_EDGE_TYPES.has(relation);
  });
  if (addedReportingEdge) return true;

  const connectors = doc.getMap('connectors');
  return delta.edgeRemovals.some((removal) => {
    const raw = connectors.get(removal.edgeId as string);
    const connector = readYjsValue<Record<string, unknown>>(raw as Y.AbstractType<unknown> | Record<string, unknown>);
    return isObjectRecord(connector) && isReportingConnector(connector);
  });
}

/**
 * Convert a NodeAddition to Yjs-compatible node data
 */
function getDefaultNodeDimensions(type: string): { width: number; height: number } {
  switch (type) {
    case 'org_position':
    case 'org_person_photo':
    case 'org_person_text':
      return { width: 270, height: 80 };
    case 'org_department':
      return { width: 280, height: 60 };
    default:
      return { width: 200, height: 80 };
  }
}

function additionToYjsNode(addition: NodeAddition): Record<string, unknown> {
  const dims = getDefaultNodeDimensions(addition.type);
  return {
    id: addition.id,
    type: addition.type,
    x: addition.position?.x ?? 0,
    y: addition.position?.y ?? 0,
    w: dims.width,
    h: dims.height,
    width: dims.width,
    height: dims.height,
    name: addition.name,
    text: addition.title ?? addition.name,
    category: 'orgchart',
    attrs: {
      name: addition.name,
      title: addition.title,
      department: addition.department,
      ...addition.props,
    },
    props: {
      name: addition.name,
      title: addition.title,
      department: addition.department,
      ...addition.props,
    },
  };
}

function additionToCsvCacheRecord(addition: NodeAddition): Record<string, unknown> {
  return {
    personId: addition.id,
    name: addition.name,
    role: addition.title ?? addition.name,
    title: addition.title,
    department: addition.department,
    ...(addition.managerId ? { managerId: addition.managerId, reportsTo: addition.managerId } : {}),
    ...(addition.props ?? {}),
  };
}

function readCsvChildIds(csvChildren: Y.Map<unknown>, nodeId: string): string[] {
  const value = csvChildren.get(nodeId);
  return Array.isArray(value) ? [...value] : [];
}

function writeCsvChildIds(csvChildren: Y.Map<unknown>, nodeId: string, childIds: string[]): void {
  csvChildren.set(nodeId, childIds);
}

function addCsvChild(csvChildren: Y.Map<unknown>, parentId: string, childId: string): boolean {
  const childIds = readCsvChildIds(csvChildren, parentId);
  if (childIds.includes(childId)) return false;
  childIds.push(childId);
  writeCsvChildIds(csvChildren, parentId, childIds);
  return true;
}

function removeCsvChild(csvChildren: Y.Map<unknown>, parentId: string, childId: string): boolean {
  const childIds = readCsvChildIds(csvChildren, parentId);
  if (!childIds.includes(childId)) return false;
  writeCsvChildIds(csvChildren, parentId, childIds.filter((id) => id !== childId));
  return true;
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function isReportingConnector(connector: Record<string, unknown>): boolean {
  const edgeType = String(connector.type ?? '');
  const attrs = (connector.attrs as Record<string, unknown> | undefined) ?? {};
  const relation = String(attrs.relation ?? '');
  return (
    edgeType === 'reporting' ||
    edgeType === 'reports_to' ||
    relation === 'reporting' ||
    relation === 'reports_to'
  );
}

function readConnectorEndpoints(
  connector: Record<string, unknown>,
): { from: string | null; to: string | null } {
  const fromRaw = connector.sourceShapeId ?? connector.source ?? connector.fromId ?? connector.from;
  const toRaw = connector.targetShapeId ?? connector.target ?? connector.toId ?? connector.to;
  const from = typeof fromRaw === 'string' ? fromRaw : null;
  const to = typeof toRaw === 'string' ? toRaw : null;
  return { from, to };
}

function connectorToEdgeRecord(connectorId: string, raw: unknown): Edge | null {
  if (!isObjectRecord(raw)) return null;
  const { from, to } = readConnectorEndpoints(raw);
  if (!from || !to) return null;

  const props = isObjectRecord(raw.props) ? (raw.props as Record<string, unknown>) : undefined;
  const nestedAttrs = isObjectRecord(props?.attrs) ? (props?.attrs as Record<string, unknown>) : undefined;
  const attrsFromProps =
    props && !nestedAttrs && !('fromAnchor' in props) && !('toAnchor' in props) ? props : undefined;
  const attrs = (nestedAttrs ?? attrsFromProps ?? (isObjectRecord(raw.attrs) ? raw.attrs : {})) as Record<
    string,
    unknown
  >;

  return {
    id: connectorId,
    type: (raw.type as Edge['type']) ?? 'line',
    from,
    to,
    attrs,
    fromAnchor: (raw.fromAnchor as Edge['fromAnchor']) ?? (props?.fromAnchor as Edge['fromAnchor'] | undefined),
    toAnchor: (raw.toAnchor as Edge['toAnchor']) ?? (props?.toAnchor as Edge['toAnchor'] | undefined),
    fromOffset: (raw.fromOffset as number | undefined) ?? (props?.fromOffset as number | undefined),
    toOffset: (raw.toOffset as number | undefined) ?? (props?.toOffset as number | undefined),
  };
}

function shapeToNodeRecord(shapeId: string, raw: unknown): Node | null {
  if (!isObjectRecord(raw)) return null;
  return {
    id: shapeId,
    type: (raw.type as string) ?? 'shape',
    version: typeof raw.version === 'number' ? raw.version : undefined,
    name: typeof raw.name === 'string' ? raw.name : undefined,
    text: typeof raw.text === 'string' ? raw.text : undefined,
    attrs: isObjectRecord(raw.attrs) ? (raw.attrs as Record<string, unknown>) : undefined,
    props: isObjectRecord(raw.props) ? (raw.props as Record<string, unknown>) : undefined,
    x: toNumber(raw.x, 0),
    y: toNumber(raw.y, 0),
    w: typeof raw.w === 'number' ? raw.w : undefined,
    h: typeof raw.h === 'number' ? raw.h : undefined,
    width: typeof raw.width === 'number' ? raw.width : undefined,
    height: typeof raw.height === 'number' ? raw.height : undefined,
    fillColor: typeof raw.fillColor === 'string' ? raw.fillColor : undefined,
    strokeColor: typeof raw.strokeColor === 'string' ? raw.strokeColor : undefined,
    sex: typeof raw.sex === 'string' ? (raw.sex as Node['sex']) : undefined,
    deceased: typeof raw.deceased === 'boolean' ? raw.deceased : undefined,
    birthYear: typeof raw.birthYear === 'number' ? raw.birthYear : undefined,
    deathYear: typeof raw.deathYear === 'number' ? raw.deathYear : undefined,
    location: typeof raw.location === 'string' ? raw.location : undefined,
  };
}

/**
 * Extract scene-compatible nodes/edges from a Y.Doc snapshot.
 */
export function extractSceneFromYDoc(doc: Y.Doc): { nodes: Node[]; edges: Edge[] } {
  const shapes = doc.getMap<unknown>('shapes');
  const connectors = doc.getMap<unknown>('connectors');
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  shapes.forEach((raw, id) => {
    const node = shapeToNodeRecord(id, readYjsValue(raw));
    if (node) nodes.push(node);
  });

  connectors.forEach((raw, id) => {
    const edge = connectorToEdgeRecord(id, raw);
    if (edge) edges.push(edge);
  });

  return { nodes, edges };
}

// =============================================================================
// Diff Computation
// =============================================================================

export interface ComputeDiffParams {
  /** The forked diagram's ID */
  forkedDiagramId: string;

  /** Current nodes in the original diagram */
  originalNodes: Node[];

  /** Current edges in the original diagram */
  originalEdges: Edge[];

  /** Current nodes in the forked diagram */
  forkedNodes: Node[];

  /** Current edges in the forked diagram */
  forkedEdges: Edge[];

  /** Nodes in the original diagram at fork time (reconstructed from fork state) */
  baseNodes: Node[];

  /** Edges in the original diagram at fork time */
  baseEdges: Edge[];
}

/**
 * Compute the differences between original and forked diagrams since the fork point
 */
export function computeDiagramDiff(params: ComputeDiffParams): DiagramDiff {
  const {
    originalNodes,
    originalEdges,
    forkedNodes,
    forkedEdges,
    baseNodes,
    baseEdges,
  } = params;

  // Build node maps for efficient lookup
  const baseNodeMap = new Map(baseNodes.map((n) => [n.id, n]));
  const originalNodeMap = new Map(originalNodes.map((n) => [n.id, n]));
  const forkedNodeMap = new Map(forkedNodes.map((n) => [n.id, n]));

  // Compute changes in original since fork
  const originalChanges = computeChanges(baseNodeMap, originalNodeMap, baseEdges, originalEdges);

  // Compute changes in forked since fork
  const forkedChanges = computeChanges(baseNodeMap, forkedNodeMap, baseEdges, forkedEdges);

  // Find conflicts (same node modified in both)
  const conflicts = findConflicts(originalChanges, forkedChanges);

  // Determine if auto-reconcile is possible
  const canAutoReconcile = conflicts.length === 0;

  return {
    originalChanges,
    forkedChanges,
    conflicts,
    canAutoReconcile,
  };
}

/**
 * Compute changes between base state and current state
 */
function computeChanges(
  baseMap: Map<string, Node>,
  currentMap: Map<string, Node>,
  baseEdges: Edge[],
  currentEdges: Edge[],
): DiagramChange[] {
  const changes: DiagramChange[] = [];
  const baseManagerByNode = buildManagerByNode(baseEdges);
  const currentManagerByNode = buildManagerByNode(currentEdges);

  // Find added and modified nodes
  currentMap.forEach((currentNode, nodeId) => {
    const baseNode = baseMap.get(nodeId);

    if (!baseNode) {
      // Node was added
      changes.push({
        type: 'added',
        nodeId,
        nodeName: currentNode.name || nodeId,
        nodeType: currentNode.type,
        details: `Added ${currentNode.type}`,
      });
    } else {
      // Check if node was modified
      const changedFields = getChangedFields(baseNode, currentNode);
      if (changedFields.length > 0) {
        changes.push({
          type: 'modified',
          nodeId,
          nodeName: currentNode.name || nodeId,
          nodeType: currentNode.type,
          details: `Modified: ${changedFields.join(', ')}`,
          changedFields,
        });
      }

      const oldParentId = baseManagerByNode.get(nodeId) ?? null;
      const newParentId = currentManagerByNode.get(nodeId) ?? null;
      if (oldParentId !== newParentId) {
        changes.push({
          type: 'moved',
          nodeId,
          nodeName: currentNode.name || nodeId,
          nodeType: currentNode.type,
          details: `Manager changed: ${oldParentId ?? 'none'} -> ${newParentId ?? 'none'}`,
          oldParentId,
          newParentId,
        });
      }
    }
  });

  // Find removed nodes
  baseMap.forEach((baseNode, nodeId) => {
    if (!currentMap.has(nodeId)) {
      changes.push({
        type: 'removed',
        nodeId,
        nodeName: baseNode.name || nodeId,
        nodeType: baseNode.type,
        details: `Removed ${baseNode.type}`,
      });
    }
  });

  return changes;
}

function buildManagerByNode(edges: Edge[]): Map<string, string | null> {
  const managerByNode = new Map<string, string | null>();
  for (const edge of edges) {
    const edgeType = edge.type as string;
    if (!REPORTING_EDGE_TYPES.has(edgeType)) continue;
    managerByNode.set(edge.from, edge.to ?? null);
  }
  return managerByNode;
}

/**
 * Get list of fields that changed between two nodes
 */
function getChangedFields(baseNode: Node, currentNode: Node): string[] {
  const changedFields: string[] = [];

  // Check top-level fields
  const fieldsToCheck = ['name', 'text', 'x', 'y', 'w', 'h'] as const;
  for (const field of fieldsToCheck) {
    if (baseNode[field] !== currentNode[field]) {
      changedFields.push(field);
    }
  }

  // Check attrs
  const baseAttrs = (baseNode.attrs || {}) as Record<string, unknown>;
  const currentAttrs = (currentNode.attrs || {}) as Record<string, unknown>;

  const attrKeys = new Set([...Object.keys(baseAttrs), ...Object.keys(currentAttrs)]);
  for (const key of attrKeys) {
    if (JSON.stringify(baseAttrs[key]) !== JSON.stringify(currentAttrs[key])) {
      changedFields.push(`attrs.${key}`);
    }
  }

  return changedFields;
}

/**
 * Find conflicts between original and forked changes
 */
function findConflicts(
  originalChanges: DiagramChange[],
  forkedChanges: DiagramChange[]
): ConflictInfo[] {
  const conflicts: ConflictInfo[] = [];

  const toConflictMap = (changes: DiagramChange[]) => {
    const map = new Map<string, DiagramChange>();
    for (const change of changes) {
      const existing = map.get(change.nodeId);
      if (!existing || getConflictPriority(change) > getConflictPriority(existing)) {
        map.set(change.nodeId, change);
      }
    }
    return map;
  };

  const originalChangeMap = toConflictMap(originalChanges);
  const forkedChangeMap = toConflictMap(forkedChanges);

  // Find nodes that were changed in both
  originalChangeMap.forEach((originalChange, nodeId) => {
    const forkedChange = forkedChangeMap.get(nodeId);
    if (forkedChange) {
      // Both modified the same node - this is a conflict
      conflicts.push({
        nodeId,
        nodeName: originalChange.nodeName,
        originalChange,
        forkedChange,
        suggestedResolution: suggestResolution(originalChange, forkedChange),
      });
    }
  });

  return conflicts;
}

function getConflictPriority(change: DiagramChange): number {
  if (change.type === 'removed') return 4;
  if (change.type === 'moved') return 3;
  if (change.type === 'modified') return 2;
  return 1;
}

/**
 * Suggest a resolution for a conflict
 */
function suggestResolution(
  originalChange: DiagramChange,
  forkedChange: DiagramChange
): 'keep-original' | 'keep-forked' | 'manual' {
  // If one side just removed, prefer keeping the other side's changes
  if (originalChange.type === 'removed' && forkedChange.type !== 'removed') {
    return 'keep-forked';
  }
  if (forkedChange.type === 'removed' && originalChange.type !== 'removed') {
    return 'keep-original';
  }

  // If both modified different fields, we might be able to merge (but mark as manual for safety)
  if (originalChange.type === 'modified' && forkedChange.type === 'modified') {
    const originalFields = new Set(originalChange.changedFields || []);
    const forkedFields = new Set(forkedChange.changedFields || []);

    // Check if there's any overlap
    const hasOverlap = [...originalFields].some((f) => forkedFields.has(f));
    if (!hasOverlap) {
      // No overlap - could potentially merge, but be safe
      return 'manual';
    }
  }

  if (originalChange.type === 'moved' && forkedChange.type === 'moved') {
    if (originalChange.newParentId === forkedChange.newParentId) {
      return 'keep-forked';
    }
    return 'manual';
  }

  return 'manual';
}

// =============================================================================
// Reconciliation
// =============================================================================

export interface ReconcileParams {
  /** Target Yjs document to apply changes to */
  targetDoc: Y.Doc;

  /** The diff between diagrams */
  diff: DiagramDiff;

  /** Reconciliation options */
  options: ReconcileOptions;

  /** Source nodes to pull from (for 'pull' direction) */
  sourceNodes?: Node[];

  /** Source edges to pull from (for 'pull' direction) */
  sourceEdges?: Edge[];
}

/**
 * Reconcile changes between original and forked diagrams
 */
export function reconcileDiagrams(params: ReconcileParams): ReconcileResult {
  const { targetDoc, diff, options, sourceNodes, sourceEdges } = params;

  const unresolvedConflicts: ConflictInfo[] = [];
  let appliedChanges = 0;
  let skippedChanges = 0;

  try {
    const shapeAdapter = new ShapeMapAdapter(targetDoc.getMap<unknown>('shapes'));
    const shapes = new ShapePersistenceGateway(shapeAdapter);
    const connectors = targetDoc.getMap<unknown>('connectors');

    targetDoc.transact(() => {
      // Determine which changes to apply based on direction
      const changesToApply =
        options.direction === 'pull' ? diff.originalChanges : diff.forkedChanges;

      for (const change of changesToApply) {
        // Check if this is a conflicting change
        const conflict = diff.conflicts.find((c) => c.nodeId === change.nodeId);

        if (conflict) {
          // Handle conflict based on resolution strategy
          const resolution =
            options.manualResolutions?.get(change.nodeId) || options.conflictResolution;

          if (resolution === 'manual') {
            unresolvedConflicts.push(conflict);
            skippedChanges++;
            continue;
          }

          // For 'pull', keep-original means apply the change
          // For 'push', keep-forked means apply the change
          const shouldApply =
            (options.direction === 'pull' && resolution === 'keep-original') ||
            (options.direction === 'push' && resolution === 'keep-forked');

          if (!shouldApply) {
            skippedChanges++;
            continue;
          }
        }

        if (change.type === 'removed') {
          shapes.delete(change.nodeId);
          removeConnectorsForNode(connectors, change.nodeId);
          appliedChanges++;
          continue;
        }

        if (!sourceNodes) {
          skippedChanges++;
          continue;
        }

        const sourceNode = sourceNodes.find((n) => n.id === change.nodeId);
        if (!sourceNode) {
          skippedChanges++;
          continue;
        }

        // For added/modified/moved we copy source node state and reconcile its connectors.
        shapes.upsert(nodeToYjsData(sourceNode));
        if (sourceEdges) {
          syncConnectorsForNode(connectors, shapeAdapter.raw, change.nodeId, sourceEdges);
        }
        appliedChanges++;
      }
    }, 'reconcile');

    return {
      success: unresolvedConflicts.length === 0,
      appliedChanges,
      skippedChanges,
      unresolvedConflicts,
    };
  } catch (error) {
    return {
      success: false,
      appliedChanges,
      skippedChanges,
      unresolvedConflicts,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

function removeConnectorsForNode(connectors: Y.Map<unknown>, nodeId: string): void {
  const keysToDelete: string[] = [];
  connectors.forEach((raw, key) => {
    if (!isObjectRecord(raw)) return;
    const { from, to } = readConnectorEndpoints(raw);
    if (from === nodeId || to === nodeId) {
      keysToDelete.push(key);
    }
  });
  keysToDelete.forEach((key) => connectors.delete(key));
}

function syncConnectorsForNode(
  connectors: Y.Map<unknown>,
  shapes: Y.Map<unknown>,
  nodeId: string,
  sourceEdges: Edge[],
): void {
  removeConnectorsForNode(connectors, nodeId);
  const currentNodeIds = new Set<string>();
  shapes.forEach((_raw, key) => currentNodeIds.add(key));

  for (const edge of sourceEdges) {
    if (edge.from !== nodeId && edge.to !== nodeId) continue;
    if (!currentNodeIds.has(edge.from) || !currentNodeIds.has(edge.to)) continue;
    connectors.set(edge.id, edgeToYjsData(edge));
  }
}

/**
 * Convert a Node to Yjs-compatible data
 */
function nodeToYjsData(node: Node): Record<string, unknown> {
  return {
    id: node.id,
    type: node.type,
    x: node.x,
    y: node.y,
    w: node.w,
    h: node.h,
    width: node.width,
    height: node.height,
    name: node.name,
    text: node.text,
    attrs: node.attrs,
    props: node.props,
  };
}

// =============================================================================
// Utility Exports
// =============================================================================

export { loadForkMetadata, saveForkMetadata } from '../models/DiagramFork';
